import React, { useState, useEffect, useRef } from "react";
import styles from "./AgencyTemplate.module.css";

interface IconProps {
  size?: number;
  color?: string;
}

interface AnimatedNumProps {
  target: number;
  suffix?: string;
}

interface FadeInProps {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}

interface NavItem {
  id: string;
  label: string;
}

interface Destination {
  dest: string;
  days: string;
  price: number;
  rating: number;
  reviews: number;
  tag: string;
  gradient: string;
  emoji: string;
}

interface OfferData {
  title: string;
  desc: string;
  code: string;
  iconColor: string;
  IconComp: React.FC<IconProps>;
  bg: string;
}

interface HeroStatData {
  val: number;
  suffix: string;
  label: string;
}

interface AboutStatData {
  val: number;
  suffix: string;
  label: string;
  color: string;
}

interface AboutCardData {
  icon: React.ReactNode;
  title: string;
  text: string;
  bg: string;
}

interface ContactCardData {
  icon: React.ReactNode;
  label: string;
  value: string;
  bg: string;
}

interface SearchTabData {
  id: string;
  label: string;
  Icon: React.FC<IconProps>;
}

const PLAN = {
  name: "Pro",
  modules: {
    homePage: true,
    flightEngine: true,
    packageEngine: true,
    specialOffers: true,
    aboutPage: true,
    contactPage: true,
    queryForm: true,
  },
} as const;

const agencyName = "TravelWala";
const domain = "travelwala.com";


const PlaneIcon: React.FC<IconProps> = ({ size = 24, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path
      d="M21.5 3.5L10.3 14.7M21.5 3.5L15 21l-4.7-6.3M21.5 3.5L3 9l6.3 4.7"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const GlobeIcon: React.FC<IconProps> = ({ size = 24, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="1.5" />
    <ellipse cx="12" cy="12" rx="4" ry="10" stroke={color} strokeWidth="1.5" />
    <path d="M2 12h20M3.5 7h17M3.5 17h17" stroke={color} strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const ShieldIcon: React.FC<IconProps> = ({ size = 24, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path
      d="M12 2L3 7v5c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-9-5z"
      stroke={color}
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path d="M9 12l2 2 4-4" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const HeadsetIcon: React.FC<IconProps> = ({ size = 24, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M3 18v-6a9 9 0 1118 0v6" stroke={color} strokeWidth="1.5" />
    <path
      d="M21 19a2 2 0 01-2 2h-1a2 2 0 01-2-2v-3a2 2 0 012-2h3v5zM3 19a2 2 0 002 2h1a2 2 0 002-2v-3a2 2 0 00-2-2H3v5z"
      stroke={color}
      strokeWidth="1.5"
    />
  </svg>
);

const MapPinIcon: React.FC<IconProps> = ({ size = 24, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path
      d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
      stroke={color}
      strokeWidth="1.5"
    />
    <circle cx="12" cy="9" r="2.5" stroke={color} strokeWidth="1.5" />
  </svg>
);

const CalendarIcon: React.FC<IconProps> = ({ size = 24, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="3" y="4" width="18" height="18" rx="2" stroke={color} strokeWidth="1.5" />
    <path d="M16 2v4M8 2v4M3 10h18" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const UsersIcon: React.FC<IconProps> = ({ size = 24, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="9" cy="7" r="4" stroke={color} strokeWidth="1.5" />
    <path d="M2 21v-2a4 4 0 014-4h6a4 4 0 014 4v2" stroke={color} strokeWidth="1.5" />
    <circle cx="19" cy="7" r="3" stroke={color} strokeWidth="1.5" />
    <path d="M22 21v-1.5a3.5 3.5 0 00-2.5-3.36" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const SwapIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path
      d="M7 16l-4-4m0 0l4-4m-4 4h18M17 8l4 4m0 0l-4 4m4-4H3"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SearchIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" />
    <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const StarIcon: React.FC<{ size?: number; filled?: boolean }> = ({ size = 16, filled = true }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? "#F59E0B" : "none"} stroke="#F59E0B" strokeWidth="2">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const ChevronDown: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ClockIcon: React.FC<IconProps> = ({ size = 18, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="1.5" />
    <path d="M12 6v6l4 2" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const PhoneIcon: React.FC<IconProps> = ({ size = 20, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path
      d="M22 16.92v3a2 2 0 01-2.18 2 19.86 19.86 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.86 19.86 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.13.81.36 1.6.68 2.35a2 2 0 01-.45 2.11L8.09 9.43a16 16 0 006.48 6.48l1.25-1.25a2 2 0 012.11-.45c.75.32 1.54.55 2.35.68a2 2 0 011.72 2.03z"
      stroke={color}
      strokeWidth="1.5"
    />
  </svg>
);

const MailIcon: React.FC<IconProps> = ({ size = 20, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect x="2" y="4" width="20" height="16" rx="2" stroke={color} strokeWidth="1.5" />
    <path d="M22 7l-10 6L2 7" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const ArrowRight: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M5 12h14m0 0l-6-6m6 6l-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SparkleIcon: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0L14.59 8.41L23 12L14.59 15.59L12 24L9.41 15.59L1 12L9.41 8.41L12 0Z" />
  </svg>
);

const MenuIcon: React.FC = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

/* ═══════════════════════════════════════════════════
   UTILITY COMPONENTS
   ═══════════════════════════════════════════════════ */

const AnimatedNum: React.FC<AnimatedNumProps> = ({ target, suffix = "" }) => {
  const [val, setVal] = useState<number>(0);
  const ref = useRef<HTMLSpanElement>(null);
  const [started, setStarted] = useState<boolean>(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([e]: IntersectionObserverEntry[]) => {
        if (e.isIntersecting) setStarted(true);
      },
      { threshold: 0.4 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!started) return;
    let frame: number;
    const dur = 1600;
    const startTime: number = performance.now();
    const step = (now: number): void => {
      const p: number = Math.min((now - startTime) / dur, 1);
      const ease: number = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(target * ease));
      if (p < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [started, target]);

  return (
    <span ref={ref}>
      {val.toLocaleString()}
      {suffix}
    </span>
  );
};

const FadeIn: React.FC<FadeInProps> = ({ children, delay = 0, className = "" }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [vis, setVis] = useState<boolean>(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([e]: IntersectionObserverEntry[]) => {
        if (e.isIntersecting) setVis(true);
      },
      { threshold: 0.15 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: vis ? 1 : 0,
        transform: vis ? "translateY(0)" : "translateY(32px)",
        transition: `all 0.7s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s`,
      }}
    >
      {children}
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   DATA
   ═══════════════════════════════════════════════════ */

const destinations: Destination[] = [
  { dest: "Dubai", days: "5N / 6D", price: 899, rating: 4.9, reviews: 2840, tag: "Bestseller", gradient: "linear-gradient(135deg, #0C1445 0%, #1A3A7A 50%, #D4A853 100%)", emoji: "🏙️" },
  { dest: "Bali", days: "6N / 7D", price: 1199, rating: 4.8, reviews: 1926, tag: "Trending", gradient: "linear-gradient(135deg, #0B4F3C 0%, #1A8B65 50%, #7DD4AA 100%)", emoji: "🌴" },
  { dest: "Istanbul", days: "4N / 5D", price: 749, rating: 4.7, reviews: 1580, tag: "New", gradient: "linear-gradient(135deg, #6B1D3A 0%, #C4456A 50%, #F4A7B9 100%)", emoji: "🕌" },
  { dest: "Maldives", days: "4N / 5D", price: 1499, rating: 4.9, reviews: 3200, tag: "Luxury", gradient: "linear-gradient(135deg, #052E4A 0%, #0F6B99 50%, #48D1CC 100%)", emoji: "🌊" },
];

const offersData: OfferData[] = [
  { title: "Early Bird Discount", desc: "Book 60 days ahead — save up to 25% on international flights", code: "EARLY25", iconColor: "#1D4ED8", IconComp: CalendarIcon, bg: "linear-gradient(135deg, #EFF6FF, #DBEAFE)" },
  { title: "Weekend Getaway", desc: "Flat 15% off on domestic round-trips booked for weekends", code: "WKND15", iconColor: "#EA580C", IconComp: PlaneIcon, bg: "linear-gradient(135deg, #FFF7ED, #FED7AA)" },
  { title: "Family Package", desc: "Kids fly free on select routes when booking for 2+ adults", code: "FAMILY0", iconColor: "#059669", IconComp: UsersIcon, bg: "linear-gradient(135deg, #ECFDF5, #A7F3D0)" },
];

const navItems: NavItem[] = [
  { id: "home", label: "Home" },
  { id: "flights", label: "Flights" },
  { id: "packages", label: "Packages" },
  { id: "offers", label: "Offers" },
  { id: "about", label: "About" },
  { id: "contact", label: "Contact" },
];

const heroStats: HeroStatData[] = [
  { val: 120, suffix: "+", label: "Destinations" },
  { val: 50, suffix: "K+", label: "Happy Travellers" },
  { val: 4.9, suffix: "", label: "Avg Rating" },
];

const aboutStatsData: AboutStatData[] = [
  { val: 50, suffix: "K+", label: "Happy Customers", color: "#1D4ED8" },
  { val: 120, suffix: "+", label: "Destinations", color: "#EA580C" },
  { val: 99, suffix: "%", label: "Satisfaction", color: "#059669" },
];

const searchTabs: SearchTabData[] = [
  { id: "flights", label: "Flights", Icon: PlaneIcon },
  { id: "hotels", label: "Flight + Hotel", Icon: GlobeIcon },
];

const tripOptions = ["round", "oneway", "multi"] as const;
type TripOption = typeof tripOptions[number];

const tripLabels: Record<TripOption, string> = {
  round: "Round Trip",
  oneway: "One Way",
  multi: "Multi-City",
};

/* ═══════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════ */

const AgencyTemplate: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>("home");
  const [mobileNav, setMobileNav] = useState<boolean>(false);
  const [scrolled, setScrolled] = useState<boolean>(false);
  const [tripType, setTripType] = useState<TripOption>("round");
  const [activeTab, setActiveTab] = useState<string>("flights");

  useEffect(() => {
    const onScroll = (): void => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id: string): void => {
    setActiveSection(id);
    setMobileNav(false);
    document.getElementById(`s-${id}`)?.scrollIntoView({ behavior: "smooth" });
  };

  const handleKeyNav = (id: string) => (e: React.KeyboardEvent): void => {
    if (e.key === "Enter" || e.key === " ") scrollTo(id);
  };

  const aboutCards: AboutCardData[] = [
    { icon: <GlobeIcon size={32} color="#1D4ED8" />, title: "Global GDS Network", text: "Connected to Amadeus, Sabre & Travelport for real-time inventory", bg: "#EFF6FF" },
    { icon: <ShieldIcon size={32} color="#059669" />, title: "Secure Payments", text: "PCI-compliant processing with 256-bit SSL encryption", bg: "#ECFDF5" },
    { icon: <HeadsetIcon size={32} color="#EA580C" />, title: "24/7 Support", text: "Round-the-clock assistance via phone, email & live chat", bg: "#FFF7ED" },
  ];

  const contactCards: ContactCardData[] = [
    { icon: <MailIcon size={28} color="#1D4ED8" />, label: "Email", value: `info@${domain}`, bg: "#EFF6FF" },
    { icon: <PhoneIcon size={28} color="#059669" />, label: "Phone", value: "+91 98765 43210", bg: "#ECFDF5" },
    { icon: <MapPinIcon size={28} color="#EA580C" />, label: "Address", value: "123 Travel Street, Business Bay", bg: "#FFF7ED" },
    { icon: <ClockIcon size={28} color="#7C3AED" />, label: "Hours", value: "Mon – Sat, 9 AM – 7 PM", bg: "#F5F3FF" },
  ];

  return (
    <div className={styles.site}>

      {/* ── PREVIEW BANNER ── */}
      <div className={styles.previewBanner}>
        <div className={styles.previewDot} />
        <span>Preview Mode</span>
        <span className={styles.previewSep}>•</span>
        <span className={styles.previewPlanName}>{PLAN.name} Plan</span>
        <span className={styles.previewSep}>•</span>
        <span>{domain}</span>
        <span className={styles.previewBadge}>Flight + Hotel</span>
      </div>

      {/* ── HEADER ── */}
      <header className={`${styles.header} ${scrolled ? styles.headerScrolled : ""}`}>
        <div className={styles.headerInner}>
          <div
            className={styles.brand}
            role="button"
            tabIndex={0}
            onClick={() => scrollTo("home")}
            onKeyDown={handleKeyNav("home")}
          >
            <div className={styles.brandIcon}>
              <PlaneIcon size={22} color="#fff" />
            </div>
            <div>
              <div className={styles.brandName}>{agencyName}</div>
              <div className={styles.brandTag}>TRAVEL &amp; TOURISM</div>
            </div>
          </div>

          <nav className={styles.nav}>
            {navItems.map((n: NavItem) => (
              <button
                key={n.id}
                onClick={() => scrollTo(n.id)}
                className={`${styles.navLink} ${activeSection === n.id ? styles.navLinkActive : ""}`}
              >
                {n.label}
                {activeSection === n.id && <div className={styles.navDot} />}
              </button>
            ))}
          </nav>

          <div className={styles.headerRight}>
            <button className={styles.ctaBtn}>
              <PlaneIcon size={16} color="#fff" />
              <span>Book Now</span>
            </button>
            <button
              className={styles.mobileToggle}
              onClick={() => setMobileNav((prev: boolean) => !prev)}
              aria-label="Toggle navigation"
            >
              <MenuIcon />
            </button>
          </div>
        </div>

        {mobileNav && (
          <div className={styles.mobileMenu}>
            {navItems.map((n: NavItem) => (
              <button key={n.id} className={styles.mobileLink} onClick={() => scrollTo(n.id)}>
                {n.label}
              </button>
            ))}
          </div>
        )}
      </header>

      <main>

        {/* ══════════════════════════════════
            HERO
            ══════════════════════════════════ */}
        <section id="s-home" className={styles.hero}>
          <div className={styles.heroBgWrap}>
            <div className={styles.heroGrad1} />
            <div className={styles.heroGrad2} />
            <div className={styles.heroGrad3} />
            <div className={styles.heroPattern} />
            <div className={styles.floatingEl1}>
              <PlaneIcon size={40} color="rgba(29,78,216,0.12)" />
            </div>
            <div className={styles.floatingEl2}>
              <GlobeIcon size={48} color="rgba(249,115,22,0.1)" />
            </div>
            <div className={styles.floatingEl3}>
              <MapPinIcon size={32} color="rgba(29,78,216,0.08)" />
            </div>
          </div>

          <div className={styles.heroContent}>
            <FadeIn>
              <div className={styles.heroBadge}>
                <SparkleIcon size={14} />
                <span>Trusted by 10,000+ Travellers Worldwide</span>
              </div>
            </FadeIn>
            <FadeIn delay={0.1}>
              <h1 className={styles.heroTitle}>
                Discover the World<br />
                with <span className={styles.heroAccent}>{agencyName}</span>
              </h1>
            </FadeIn>
            <FadeIn delay={0.2}>
              <p className={styles.heroDesc}>
                Book flights, hotels, and curated holiday packages at unbeatable prices.
                Your next adventure is just a search away.
              </p>
            </FadeIn>
            <FadeIn delay={0.3}>
              <div className={styles.heroStats}>
                {heroStats.map((s: HeroStatData, i: number) => (
                  <div key={i} className={styles.heroStat}>
                    <div className={styles.heroStatVal}>
                      <AnimatedNum target={s.val} suffix={s.suffix} />
                    </div>
                    <div className={styles.heroStatLabel}>{s.label}</div>
                  </div>
                ))}
              </div>
            </FadeIn>
          </div>
        </section>

        {/* ══════════════════════════════════
            SEARCH
            ══════════════════════════════════ */}
        <section id="s-flights" className={styles.searchSection}>
          <FadeIn>
            <div className={styles.searchCard}>
              <div className={styles.searchHeader}>
                <div className={styles.searchTabs}>
                  {searchTabs.map((t: SearchTabData) => {
                    const TabIcon = t.Icon;
                    return (
                      <button
                        key={t.id}
                        onClick={() => setActiveTab(t.id)}
                        className={`${styles.searchTab} ${activeTab === t.id ? styles.searchTabActive : ""}`}
                      >
                        <TabIcon size={18} />
                        <span>{t.label}</span>
                      </button>
                    );
                  })}
                </div>
                <div className={styles.tripTypes}>
                  {tripOptions.map((t: TripOption) => (
                    <button
                      key={t}
                      onClick={() => setTripType(t)}
                      className={`${styles.tripType} ${tripType === t ? styles.tripTypeActive : ""}`}
                    >
                      {tripLabels[t]}
                    </button>
                  ))}
                </div>
              </div>

              <div className={styles.searchForm}>
                <div className={styles.searchFieldGroup}>
                  <div className={styles.searchField}>
                    <label className={styles.searchLabel}>FROM</label>
                    <div className={styles.searchInputWrap}>
                      <input className={styles.searchInput} defaultValue="Delhi (DEL)" placeholder="City or Airport" />
                      <span className={styles.searchInputSub}>Indira Gandhi International</span>
                    </div>
                  </div>
                  <button className={styles.swapBtn} aria-label="Swap destinations">
                    <SwapIcon />
                  </button>
                  <div className={styles.searchField}>
                    <label className={styles.searchLabel}>TO</label>
                    <div className={styles.searchInputWrap}>
                      <input className={styles.searchInput} defaultValue="Dubai (DXB)" placeholder="City or Airport" />
                      <span className={styles.searchInputSub}>Dubai International</span>
                    </div>
                  </div>
                </div>
                <div className={styles.searchFieldGroup2}>
                  <div className={styles.searchFieldSm}>
                    <label className={styles.searchLabel}>DEPART</label>
                    <input className={styles.searchInputSm} type="date" defaultValue="2026-04-15" />
                  </div>
                  {tripType !== "oneway" && (
                    <div className={styles.searchFieldSm}>
                      <label className={styles.searchLabel}>RETURN</label>
                      <input className={styles.searchInputSm} type="date" defaultValue="2026-04-22" />
                    </div>
                  )}
                  <div className={styles.searchFieldSm}>
                    <label className={styles.searchLabel}>TRAVELLERS</label>
                    <div className={styles.travellersInput}>
                      <UsersIcon size={16} color="#64748B" />
                      <span>2 Adults</span>
                      <ChevronDown size={14} />
                    </div>
                  </div>
                  <button className={styles.searchBtn}>
                    <SearchIcon size={20} />
                    <span>Search</span>
                  </button>
                </div>
              </div>
            </div>
          </FadeIn>
        </section>

        {/* ══════════════════════════════════
            PACKAGES
            ══════════════════════════════════ */}
        <section id="s-packages" className={styles.packagesSection}>
          <div className={styles.inner}>
            <FadeIn>
              <div className={styles.sectionHeader}>
                <div className={styles.sectionTag}>
                  <MapPinIcon size={14} /> Holiday Packages
                </div>
                <h2 className={styles.sectionTitle}>Explore Popular Destinations</h2>
                <p className={styles.sectionDesc}>
                  Curated flight + hotel packages handpicked for unforgettable experiences
                </p>
              </div>
            </FadeIn>
            <div className={styles.packageGrid}>
              {destinations.map((pkg: Destination, i: number) => (
                <FadeIn key={pkg.dest} delay={i * 0.1}>
                  <div className={styles.packageCard}>
                    <div className={styles.packageImg} style={{ background: pkg.gradient }}>
                      <span className={styles.packageEmoji}>{pkg.emoji}</span>
                      <div className={styles.packageTag}>
                        <SparkleIcon size={10} />
                        <span>{pkg.tag}</span>
                      </div>
                      <div className={styles.packageOverlay} />
                    </div>
                    <div className={styles.packageBody}>
                      <div className={styles.packageTop}>
                        <h3 className={styles.packageDest}>{pkg.dest}</h3>
                        <div className={styles.packageRating}>
                          <StarIcon size={14} />
                          <span className={styles.packageRatingScore}>{pkg.rating}</span>
                          <span className={styles.packageRatingCount}>({pkg.reviews.toLocaleString()})</span>
                        </div>
                      </div>
                      <div className={styles.packageMeta}>
                        <span className={styles.packageMetaItem}>
                          <CalendarIcon size={14} color="#64748B" /> {pkg.days}
                        </span>
                        <span className={styles.packageMetaItem}>
                          <PlaneIcon size={14} color="#64748B" /> Flight + Hotel
                        </span>
                      </div>
                      <div className={styles.packageFooter}>
                        <div>
                          <span className={styles.packageFrom}>from</span>
                          <span className={styles.packagePrice}>${pkg.price}</span>
                          <span className={styles.packagePer}>/person</span>
                        </div>
                        <button className={styles.packageBtn}>
                          View Deal <ArrowRight size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════
            OFFERS
            ══════════════════════════════════ */}
        <section id="s-offers" className={styles.offersSection}>
          <div className={styles.inner}>
            <FadeIn>
              <div className={styles.sectionHeader}>
                <div className={styles.sectionTag}>
                  <SparkleIcon size={14} /> Special Offers
                </div>
                <h2 className={styles.sectionTitle}>Limited Time Deals</h2>
                <p className={styles.sectionDesc}>Grab exclusive discounts before they expire</p>
              </div>
            </FadeIn>
            <div className={styles.offerGrid}>
              {offersData.map((o: OfferData, i: number) => {
                const IconComponent = o.IconComp;
                return (
                  <FadeIn key={o.code} delay={i * 0.12}>
                    <div className={styles.offerCard} style={{ background: o.bg }}>
                      <div className={styles.offerIconWrap}>
                        <IconComponent size={28} color={o.iconColor} />
                      </div>
                      <h3 className={styles.offerTitle}>{o.title}</h3>
                      <p className={styles.offerDesc}>{o.desc}</p>
                      <div className={styles.offerBottom}>
                        <div className={styles.offerCode}>
                          <span className={styles.offerCodeLabel}>USE CODE</span>
                          <span className={styles.offerCodeValue}>{o.code}</span>
                        </div>
                        <button className={styles.offerApply}>Apply</button>
                      </div>
                    </div>
                  </FadeIn>
                );
              })}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════
            ABOUT
            ══════════════════════════════════ */}
        <section id="s-about" className={styles.aboutSection}>
          <div className={styles.inner}>
            <div className={styles.aboutGrid}>
              <FadeIn>
                <div>
                  <div className={styles.sectionTag}>
                    <GlobeIcon size={14} /> About Us
                  </div>
                  <h2 className={styles.sectionTitleLeft}>
                    Your Trusted Travel<br />Partner Since 2020
                  </h2>
                  <p className={styles.aboutText}>
                    {agencyName} is a licensed travel agency providing affordable flights, hotel bookings, and holiday
                    packages to destinations worldwide. With direct GDS connectivity and real-time pricing, we ensure
                    you get the best deals every time.
                  </p>
                  <div className={styles.aboutStats}>
                    {aboutStatsData.map((s: AboutStatData, i: number) => (
                      <div key={i} className={styles.aboutStat}>
                        <div className={styles.aboutStatVal} style={{ color: s.color }}>
                          <AnimatedNum target={s.val} suffix={s.suffix} />
                        </div>
                        <div className={styles.aboutStatLabel}>{s.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </FadeIn>
              <FadeIn delay={0.15}>
                <div className={styles.aboutCards}>
                  {aboutCards.map((c: AboutCardData, i: number) => (
                    <div key={i} className={styles.aboutCard}>
                      <div className={styles.aboutCardIcon} style={{ background: c.bg }}>
                        {c.icon}
                      </div>
                      <div>
                        <div className={styles.aboutCardTitle}>{c.title}</div>
                        <div className={styles.aboutCardText}>{c.text}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </FadeIn>
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════
            CONTACT
            ══════════════════════════════════ */}
        <section id="s-contact" className={styles.contactSection}>
          <div className={styles.inner}>
            <FadeIn>
              <div className={styles.sectionHeader}>
                <div className={styles.sectionTag}>
                  <MailIcon size={14} /> Contact Us
                </div>
                <h2 className={styles.sectionTitle}>Get in Touch</h2>
                <p className={styles.sectionDesc}>We&apos;d love to help plan your next trip</p>
              </div>
            </FadeIn>
            <div className={styles.contactGrid}>
              {contactCards.map((c: ContactCardData, i: number) => (
                <FadeIn key={c.label} delay={i * 0.08}>
                  <div className={styles.contactCard}>
                    <div className={styles.contactIconWrap} style={{ background: c.bg }}>
                      {c.icon}
                    </div>
                    <div className={styles.contactLabel}>{c.label}</div>
                    <div className={styles.contactValue}>{c.value}</div>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>

      </main>

      {/* ══════════════════════════════════
          FOOTER
          ══════════════════════════════════ */}
      <footer className={styles.footer}>
        <div className={styles.footerTop}>
          <div className={styles.footerBrand}>
            <div className={styles.footerBrandRow}>
              <div className={styles.brandIconFooter}>
                <PlaneIcon size={20} color="#fff" />
              </div>
              <span className={styles.footerLogo}>{agencyName}</span>
            </div>
            <p className={styles.footerDesc}>
              Your trusted partner for flights, hotels, and holiday packages worldwide. Powered by GDS technology.
            </p>
          </div>
          <div className={styles.footerLinks}>
            <div className={styles.footerCol}>
              <div className={styles.footerColTitle}>Quick Links</div>
              {navItems.map((n: NavItem) => (
                <div
                  key={n.id}
                  className={styles.footerLink}
                  role="button"
                  tabIndex={0}
                  onClick={() => scrollTo(n.id)}
                  onKeyDown={handleKeyNav(n.id)}
                >
                  {n.label}
                </div>
              ))}
            </div>
            <div className={styles.footerCol}>
              <div className={styles.footerColTitle}>Support</div>
              {["FAQs", "Terms & Conditions", "Privacy Policy", "Refund Policy", "Cancellation"].map((l: string) => (
                <div key={l} className={styles.footerLink}>{l}</div>
              ))}
            </div>
            <div className={styles.footerCol}>
              <div className={styles.footerColTitle}>Connect</div>
              {["Facebook", "Instagram", "Twitter / X", "LinkedIn"].map((l: string) => (
                <div key={l} className={styles.footerLink}>{l}</div>
              ))}
            </div>
          </div>
        </div>
        <div className={styles.footerBottom}>
          <span>&copy; {new Date().getFullYear()} {agencyName}. All rights reserved.</span>
          <span className={styles.poweredBy}>
            Powered by <strong className={styles.poweredByBrand}>FlyGDS</strong>
          </span>
        </div>
      </footer>

    </div>
  );
};

export default AgencyTemplate;